import { useState, useEffect } from 'react';

import {
    Box,
    Typography,
    Button,
    LinearProgress
} from '@mui/material';

import { useApis } from '../../../../api/useApiClient';
import { useNotification } from '../../../../context/NotificationContext';

import VehicleIcon from '@mui/icons-material/DirectionsCar';
import ClearIcon from '@mui/icons-material/Clear';

import ItemPicker from '../../../Common/Form/ItemPicker';
import VehiclesList from '../../../Vehicle/VehiclesList';

export default function VehicleSelector({ workOrder, customerId, disabled = false, onVehicleChange }) {

    const { tenantApi } = useApis();
    const { showNotification } = useNotification();

    const [selectedVehicle, setSelectedVehicle] = useState(null);
    useEffect(() => {
        // Set selected vehicle from work order data
        setSelectedVehicle(workOrder?.vehicle || null);
    }, [workOrder]);

    // Clear selected vehicle when customer is cleared
    useEffect(() => {
        if (!customerId) {
            setSelectedVehicle(null);
        }
    }, [customerId]);

    const [loading, setLoading] = useState(false);

    function handleSelectVehicle(selectEvent) {
        const vehicle = selectEvent.target.item;
        console.debug("Setting vehicle for work order", { workOrder: workOrder.id, vehicle });

        setLoading(true);
        tenantApi(`/tenants/work-orders/${workOrder.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
                vehicleId: vehicle.id
            })
        }).then(res => {
            showNotification(`Vehicle added to the order`, 'success');
            setSelectedVehicle(vehicle);
            onVehicleChange?.(vehicle);
        }).catch(err => {
            console.error(err);
            showNotification(
                `Failed to add vehicle. ${err.message || 'Please try again.'}`,
                'error'
            );
        }).finally(() => {
            setLoading(false);
        });
    }

    function handleClearVehicle() {
        // Call update work order API and clear the vehicle
        console.debug("Clearing vehicle for work order", workOrder.id);

        setLoading(true);
        tenantApi(`/tenants/work-orders/${workOrder.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
                vehicleId: null // clear the vehicle ID from DB
            })
        }).then(res => {
            showNotification(`Vehicle removed from the order`, 'warning');
            setSelectedVehicle(null);
            onVehicleChange?.(null);
        }).catch(err => {
            console.error(err);
            showNotification(
                `Failed to remove vehicle. ${err.message || 'Please try again.'}`,
                'error'
            );
        }).finally(() => {
            setLoading(false);
        });
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" display="flex" alignItems="center" fontWeight="bold">
                    <VehicleIcon sx={{ mr: 1 }} />
                    Vehicle
                </Typography>
            </Box>

            {selectedVehicle ? (
                <Box>
                    <Box>
                        <Typography variant="subtitle1">{selectedVehicle.registrationNumber}</Typography>
                        <Typography variant="body2" color="textSecondary">
                            {selectedVehicle.make} {selectedVehicle.model} {selectedVehicle.year}
                        </Typography>
                    </Box>
                    <Button
                        onClick={handleClearVehicle}
                        disabled={disabled || loading}
                        startIcon={<ClearIcon />}
                        size="small"
                        sx={{ mt: 1 }}
                    >
                        Clear Vehicle
                    </Button>
                </Box>
            ) : (
                <ItemPicker
                    component="Button"
                    label="Select Vehicle"
                    variant='outlined'
                    ListView={VehiclesList}
                    filters={{
                        ownerId: customerId
                    }}
                    onChange={handleSelectVehicle}
                    fullWidth={false}
                    disabled={disabled || loading}
                />
            )}
            {loading && <LinearProgress />}
        </Box>
    );
}
